/**
 * Video Player Modal - View videos with coach feedback
 *
 * Features:
 * - Video playback with controls
 * - Coach comments display (timestamped)
 * - Coach annotations overlay
 * - Add athlete comments
 * - AI Analysis (paywalled for non-subscribers)
 */

import { useState, useEffect, useRef } from 'react';
import {
  getVideo,
  listComments,
  listAnnotations,
  addComment,
  updateVideo,
  deleteVideo,
  getVideoAnalysis,
  analyzeVideo,
  type Video,
  type Comment,
  type Annotation,
  type VideoAnalysisResult,
} from '@tss/shared';
import {
  X,
  Play,
  Pause,
  MessageSquare,
  Send,
  Clock,
  User,
  Edit3,
  Trash2,
  Check,
  Loader2,
  Pencil,
  Sparkles,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Crown,
} from 'lucide-react';
import { formatRelativeTime } from '@tss/shared';
import { Button, Input } from '@tss/shared/ui';
import { useSubscription } from '../hooks/useSubscription';
import { PaywallOverlay } from './UI/PaywallOverlay';

interface VideoPlayerModalProps {
  video: Video;
  onClose: () => void;
  onUpdate?: () => void;
}

export function VideoPlayerModal({ video, onClose, onUpdate }: VideoPlayerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [analysis, setAnalysis] = useState<VideoAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAIPaywall, setShowAIPaywall] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Subscription for AI Analysis paywall
  const {
    canAccess,
    aiCreditsRemaining,
    isCoachTier,
    refresh: refreshSubscription,
  } = useSubscription();
  const canViewAIAnalysis = canAccess('ai_analysis');

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Sidebar tab state: 'feedback' or 'analysis'
  const [activeTab, setActiveTab] = useState<'feedback' | 'analysis'>('feedback');

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(video.title);
  const [editDescription] = useState(video.description || '');
  const [saving, setSaving] = useState(false);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadVideoData();
  }, [video.video_id]);

  useEffect(() => {
    // Draw annotations when current time changes
    drawAnnotations();
  }, [currentTime, annotations]);

  async function loadVideoData() {
    setLoading(true);
    setError(null);
    try {
      const [videoData, commentsData, annotationsData, analysisData] = await Promise.all([
        getVideo(video.video_id),
        listComments(video.video_id).catch(() => ({ comments: [] })),
        listAnnotations(video.video_id).catch(() => ({ annotations: [] })),
        getVideoAnalysis(video.video_id).catch(() => ({ analysis: null })),
      ]);

      setPlaybackUrl(videoData.playback_url || null);
      setComments(commentsData.comments || []);
      setAnnotations(annotationsData.annotations || []);
      setAnalysis(analysisData.analysis);
    } catch (err) {
      console.error('Failed to load video:', err);
      setError('Failed to load video. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function drawAnnotations() {
    const canvas = canvasRef.current;
    const videoEl = videoRef.current;
    if (!canvas || !videoEl) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Find annotations that should be visible at current time
    const visibleAnnotations = annotations.filter((ann) => {
      const start = ann.timestamp_seconds;
      const end = start + (ann.duration_seconds || 2);
      return currentTime >= start && currentTime <= end;
    });

    // Draw each annotation
    visibleAnnotations.forEach((ann) => {
      ctx.strokeStyle = ann.color || '#ff0000';
      ctx.lineWidth = ann.stroke_width || 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const w = canvas.width;
      const h = canvas.height;

      switch (ann.annotation_type) {
        case 'CIRCLE':
          if (
            ann.data.x !== undefined &&
            ann.data.y !== undefined &&
            ann.data.radius !== undefined
          ) {
            ctx.beginPath();
            ctx.arc(
              ann.data.x * w,
              ann.data.y * h,
              ann.data.radius * Math.min(w, h),
              0,
              Math.PI * 2
            );
            ctx.stroke();
          }
          break;

        case 'ARROW':
        case 'LINE':
          if (
            ann.data.x1 !== undefined &&
            ann.data.y1 !== undefined &&
            ann.data.x2 !== undefined &&
            ann.data.y2 !== undefined
          ) {
            ctx.beginPath();
            ctx.moveTo(ann.data.x1 * w, ann.data.y1 * h);
            ctx.lineTo(ann.data.x2 * w, ann.data.y2 * h);
            ctx.stroke();

            // Draw arrowhead for ARROW type
            if (ann.annotation_type === 'ARROW') {
              const angle = Math.atan2(
                (ann.data.y2 - ann.data.y1) * h,
                (ann.data.x2 - ann.data.x1) * w
              );
              const headLen = 15;
              ctx.beginPath();
              ctx.moveTo(ann.data.x2 * w, ann.data.y2 * h);
              ctx.lineTo(
                ann.data.x2 * w - headLen * Math.cos(angle - Math.PI / 6),
                ann.data.y2 * h - headLen * Math.sin(angle - Math.PI / 6)
              );
              ctx.moveTo(ann.data.x2 * w, ann.data.y2 * h);
              ctx.lineTo(
                ann.data.x2 * w - headLen * Math.cos(angle + Math.PI / 6),
                ann.data.y2 * h - headLen * Math.sin(angle + Math.PI / 6)
              );
              ctx.stroke();
            }
          }
          break;

        case 'RECTANGLE':
          if (
            ann.data.x !== undefined &&
            ann.data.y !== undefined &&
            ann.data.width !== undefined &&
            ann.data.height !== undefined
          ) {
            ctx.strokeRect(ann.data.x * w, ann.data.y * h, ann.data.width * w, ann.data.height * h);
          }
          break;

        case 'FREEHAND':
          if (ann.data.points && ann.data.points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(ann.data.points[0].x * w, ann.data.points[0].y * h);
            ann.data.points.forEach((point) => {
              ctx.lineTo(point.x * w, point.y * h);
            });
            ctx.stroke();
          }
          break;

        case 'TEXT':
          if (ann.data.x !== undefined && ann.data.y !== undefined && ann.data.text) {
            ctx.font = `${(ann.stroke_width || 3) * 6}px sans-serif`;
            ctx.fillStyle = ann.color || '#ff0000';
            ctx.fillText(ann.data.text, ann.data.x * w, ann.data.y * h);
          }
          break;
      }
    });
  }

  function handleTimeUpdate() {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }

  function handleLoadedMetadata() {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      // Resize canvas to match video
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = videoRef.current.clientWidth;
        canvas.height = videoRef.current.clientHeight;
      }
    }
  }

  function togglePlay() {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }

  function seekTo(time: number) {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  async function handleAddComment() {
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      // addComment(videoId, content, timestampSeconds?, parentCommentId?)
      // Was passing the old object-shape; the function takes positional args.
      await addComment(video.video_id, newComment, Math.floor(currentTime));
      setNewComment('');
      // Reload comments
      const data = await listComments(video.video_id);
      setComments(data.comments || []);
    } catch (err) {
      console.error('Failed to add comment:', err);
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  }

  async function handleSaveEdit() {
    setSaving(true);
    try {
      await updateVideo(video.video_id, {
        title: editTitle,
        description: editDescription || undefined,
      });
      setIsEditing(false);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to update video:', err);
      alert('Failed to update video. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteVideo(video.video_id);
      onUpdate?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete video:', err);
      alert('Failed to delete video. Please try again.');
      setDeleting(false);
    }
  }

  async function handleRunAnalysis() {
    setRunningAnalysis(true);
    setAnalysisError(null);
    try {
      const result = await analyzeVideo(video.video_id);
      // Set analysis to processing state
      setAnalysis({
        analysis_id: result.analysis_id,
        status: 'PROCESSING',
      } as VideoAnalysisResult);
      // Refresh subscription to update credits
      refreshSubscription();
      // Poll for completion
      pollForAnalysis();
    } catch (err: unknown) {
      console.error('Failed to run analysis:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to run analysis';
      setAnalysisError(errorMessage);
    } finally {
      setRunningAnalysis(false);
    }
  }

  async function pollForAnalysis() {
    // Poll every 10 seconds for up to 5 minutes
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      attempts++;
      try {
        const data = await getVideoAnalysis(video.video_id);
        if (data.analysis) {
          setAnalysis(data.analysis);
          if (data.analysis.status !== 'PROCESSING' && data.analysis.status !== 'PENDING') {
            return; // Done polling
          }
        }
      } catch {
        // Ignore errors during polling
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, 10000);
      }
    };

    setTimeout(poll, 10000);
  }

  // Sort comments by timestamp
  const sortedComments = [...comments].sort(
    (a, b) => (a.timestamp_seconds || 0) - (b.timestamp_seconds || 0)
  );

  // Count coach comments (non-athlete)
  const coachCommentCount = comments.filter((c) => c.author_name !== 'You').length;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <Input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={{ maxWidth: '400px' }}
            />
          ) : (
            <h2
              style={{
                color: 'white',
                fontSize: '18px',
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {video.title}
            </h2>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isEditing ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                loading={saving}
                iconLeft={saving ? undefined : <Check size={16} />}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                title="Edit video"
                iconLeft={<Edit3 size={20} />}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                title="Delete video"
                iconLeft={<Trash2 size={20} />}
              />
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} iconLeft={<X size={24} />} />
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', flexWrap: 'wrap' }}>
        {/* Video section */}
        <div
          style={{
            flex: '1 1 300px',
            minWidth: '280px',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
          }}
        >
          {loading ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                className="animate-spin"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: '3px solid rgba(255, 255, 255, 0.2)',
                  borderTopColor: 'var(--purple-600)',
                }}
              />
            </div>
          ) : error ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-danger)',
              }}
            >
              {error}
            </div>
          ) : video.status !== 'READY' ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                gap: '12px',
              }}
            >
              <Clock size={48} />
              <p>Video is still processing...</p>
              <p style={{ fontSize: '14px', opacity: 0.6 }}>
                This usually takes a few minutes. Check back soon!
              </p>
            </div>
          ) : (
            <div style={{ flex: 1, position: 'relative' }}>
              <video
                ref={videoRef}
                src={playbackUrl || undefined}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  background: 'black',
                  borderRadius: '8px',
                }}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onClick={togglePlay}
              />
              {/* Annotation overlay canvas */}
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              />
              {/* Play button overlay */}
              {!isPlaying && (
                <button
                  onClick={togglePlay}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Play size={36} fill="white" color="white" />
                </button>
              )}
            </div>
          )}

          {/* Video controls */}
          {video.status === 'READY' && playbackUrl && (
            <div style={{ marginTop: '16px' }}>
              {/* Progress bar */}
              <div
                style={{
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  seekTo(percent * duration);
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${(currentTime / duration) * 100}%`,
                    background: 'var(--purple-600)',
                    borderRadius: '3px',
                  }}
                />
                {/* Comment markers */}
                {sortedComments.map((comment, i) => {
                  if (!comment.timestamp_seconds) return null;
                  const left = (comment.timestamp_seconds / duration) * 100;
                  return (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: `${left}%`,
                        top: '-3px',
                        width: '6px',
                        height: '12px',
                        background: '#f59e0b',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                      title={`${comment.author_name}: ${comment.content}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        seekTo(comment.timestamp_seconds || 0);
                      }}
                    />
                  );
                })}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={togglePlay}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'var(--purple-600)',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isPlaying ? (
                      <Pause size={20} fill="white" color="white" />
                    ) : (
                      <Play size={20} fill="white" color="white" />
                    )}
                  </button>
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {annotations.length > 0 && (
                    <span
                      style={{
                        padding: '4px 10px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        borderRadius: '20px',
                        fontSize: '12px',
                        color: '#f87171',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Pencil size={12} />
                      {annotations.length} annotations
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar with tabs */}
        <div
          style={{
            flex: '0 0 min(350px, 100%)',
            maxWidth: '350px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Tab headers */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <button
              onClick={() => setActiveTab('feedback')}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '14px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom:
                  activeTab === 'feedback'
                    ? '2px solid var(--purple-600)'
                    : '2px solid transparent',
                color: activeTab === 'feedback' ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '13px',
              }}
            >
              <MessageSquare size={16} />
              Feedback
              {coachCommentCount > 0 && (
                <span
                  style={{
                    background: 'var(--purple-600)',
                    color: 'white',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    fontSize: '11px',
                  }}
                >
                  {coachCommentCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '14px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom:
                  activeTab === 'analysis'
                    ? '2px solid var(--purple-500)'
                    : '2px solid transparent',
                color: activeTab === 'analysis' ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '13px',
              }}
            >
              <Sparkles size={16} />
              AI Analysis
              {analysis?.status === 'COMPLETED' && (
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--purple-600)',
                  }}
                />
              )}
            </button>
          </div>

          {/* Feedback Tab Content */}
          {activeTab === 'feedback' && (
            <>
              {/* Comments list */}
              <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                {sortedComments.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    <MessageSquare size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ fontSize: '14px' }}>No feedback yet</p>
                    <p style={{ fontSize: '13px', marginTop: '4px', opacity: 0.7 }}>
                      Your coach's comments will appear here
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {sortedComments.map((comment) => (
                      <div
                        key={comment.comment_id}
                        style={{
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          cursor: comment.timestamp_seconds ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                          if (comment.timestamp_seconds) {
                            seekTo(comment.timestamp_seconds);
                          }
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                          }}
                        >
                          <div
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'var(--purple-600)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <User size={14} color="white" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ color: 'white', fontWeight: '500', fontSize: '13px' }}>
                              {comment.author_name}
                            </p>
                          </div>
                          {comment.timestamp_seconds !== null && (
                            <span
                              style={{
                                padding: '2px 8px',
                                background: 'rgba(245, 158, 11, 0.2)',
                                borderRadius: '4px',
                                fontSize: '11px',
                                color: '#fbbf24',
                              }}
                            >
                              {formatTime(comment.timestamp_seconds)}
                            </span>
                          )}
                        </div>
                        <p
                          style={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '14px',
                            lineHeight: 1.5,
                          }}
                        >
                          {comment.content}
                        </p>
                        <p
                          style={{
                            color: 'rgba(255, 255, 255, 0.4)',
                            fontSize: '11px',
                            marginTop: '8px',
                          }}
                        >
                          {formatRelativeTime(comment.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add comment */}
              <div
                style={{
                  padding: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={`Add comment at ${formatTime(currentTime)}...`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                    loading={submittingComment}
                    iconLeft={submittingComment ? undefined : <Send size={18} />}
                  />
                </div>
              </div>
            </>
          )}

          {/* AI Analysis Tab Content */}
          {activeTab === 'analysis' && (
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              {/* Paywall for AI Analysis */}
              {!canViewAIAnalysis ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px 20px',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background:
                        'linear-gradient(135deg, rgba(var(--purple-rgb), 0.3) 0%, rgba(236, 72, 153, 0.3) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <Lock size={28} style={{ color: 'var(--purple-400)' }} />
                  </div>
                  <h3
                    style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '16px',
                      marginBottom: '8px',
                    }}
                  >
                    AI Video Analysis
                  </h3>
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '13px',
                      marginBottom: '20px',
                      maxWidth: '280px',
                    }}
                  >
                    Get AI-powered feedback on your technique, identify areas for improvement, and
                    track your progress.
                  </p>
                  <button
                    onClick={() => setShowAIPaywall(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background:
                        'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    <Crown size={18} />
                    Upgrade to Unlock
                  </button>
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.4)',
                      fontSize: '11px',
                      marginTop: '12px',
                    }}
                  >
                    Starting at $9.99/month
                  </p>
                </div>
              ) : !analysis ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px 20px',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background:
                        'linear-gradient(135deg, rgba(var(--purple-rgb), 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <Sparkles size={28} style={{ color: 'var(--purple-400)' }} />
                  </div>
                  <h3
                    style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '16px',
                      marginBottom: '8px',
                    }}
                  >
                    AI Video Analysis
                  </h3>
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '13px',
                      marginBottom: '6px',
                      maxWidth: '280px',
                    }}
                  >
                    Get detailed AI feedback on your technique and performance.
                  </p>
                  {isCoachTier ? (
                    <p
                      style={{ color: 'var(--purple-600)', fontSize: '12px', marginBottom: '20px' }}
                    >
                      Coaches have unlimited AI access
                    </p>
                  ) : (
                    <p
                      style={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: '12px',
                        marginBottom: '20px',
                      }}
                    >
                      Uses 1 AI credit ({aiCreditsRemaining} remaining)
                    </p>
                  )}
                  {analysisError && (
                    <p
                      style={{
                        color: '#f87171',
                        fontSize: '12px',
                        marginBottom: '12px',
                        maxWidth: '280px',
                      }}
                    >
                      {analysisError}
                    </p>
                  )}
                  <button
                    onClick={handleRunAnalysis}
                    disabled={runningAnalysis || (!isCoachTier && aiCreditsRemaining <= 0)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background:
                        isCoachTier || aiCreditsRemaining > 0
                          ? 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)'
                          : 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: isCoachTier || aiCreditsRemaining > 0 ? 'pointer' : 'not-allowed',
                      opacity: runningAnalysis ? 0.7 : 1,
                    }}
                  >
                    {runningAnalysis ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Starting Analysis...
                      </>
                    ) : !isCoachTier && aiCreditsRemaining <= 0 ? (
                      <>
                        <AlertTriangle size={18} />
                        No Credits Remaining
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Run AI Analysis
                      </>
                    )}
                  </button>
                </div>
              ) : analysis.status === 'PROCESSING' || analysis.status === 'PENDING' ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: 'rgba(255, 255, 255, 0.5)',
                  }}
                >
                  <Loader2 size={32} className="animate-spin" style={{ marginBottom: '12px' }} />
                  <p style={{ fontSize: '14px' }}>AI is analyzing your video...</p>
                  <p style={{ fontSize: '13px', marginTop: '4px', opacity: 0.7 }}>
                    This usually takes a few minutes
                  </p>
                </div>
              ) : analysis.status === 'FAILED' ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: 'rgba(239, 68, 68, 0.8)',
                  }}
                >
                  <AlertTriangle size={32} style={{ marginBottom: '12px' }} />
                  <p style={{ fontSize: '14px' }}>Analysis failed</p>
                  <p style={{ fontSize: '13px', marginTop: '4px', opacity: 0.7 }}>
                    {analysis.error_message || 'Please try again later'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Video Summary */}
                  {analysis.video_summary && (
                    <div
                      style={{
                        padding: '14px',
                        background: 'rgba(var(--purple-rgb), 0.1)',
                        borderRadius: '10px',
                        border: '1px solid rgba(var(--purple-rgb), 0.2)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px',
                        }}
                      >
                        <Sparkles size={16} style={{ color: 'var(--purple-400)' }} />
                        <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>
                          Summary
                        </span>
                      </div>
                      <p
                        style={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '13px',
                          lineHeight: 1.6,
                        }}
                      >
                        {analysis.video_summary.activity_type || 'Video'}
                        {analysis.video_summary.footage_type
                          ? ` — ${analysis.video_summary.footage_type}`
                          : ''}
                        {analysis.video_summary.location
                          ? ` at ${analysis.video_summary.location}`
                          : ''}
                      </p>
                      {analysis.video_summary.primary_activity && (
                        <p
                          style={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '11px',
                            marginTop: '8px',
                          }}
                        >
                          Activity: {analysis.video_summary.primary_activity}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Priority Focus */}
                  {analysis.priority_focus && (
                    <div
                      style={{
                        padding: '14px',
                        background: 'rgba(245, 158, 11, 0.1)',
                        borderRadius: '10px',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px',
                        }}
                      >
                        <Target size={16} style={{ color: '#fbbf24' }} />
                        <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>
                          Priority Focus
                        </span>
                      </div>
                      <p
                        style={{
                          color: '#fbbf24',
                          fontWeight: '500',
                          fontSize: '14px',
                          marginBottom: '6px',
                        }}
                      >
                        {analysis.priority_focus.top_improvement}
                      </p>
                      <p
                        style={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '13px',
                          lineHeight: 1.5,
                        }}
                      >
                        {analysis.priority_focus.reasoning}
                      </p>
                    </div>
                  )}

                  {/* Technique Analysis */}
                  {analysis.technique_analysis && analysis.technique_analysis.length > 0 && (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px',
                        }}
                      >
                        <TrendingUp size={16} style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>
                          Technique Breakdown
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {analysis.technique_analysis.map((tech, i) => (
                          <div
                            key={i}
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '6px',
                              }}
                            >
                              <span style={{ color: 'white', fontWeight: '500', fontSize: '13px' }}>
                                {tech.movement}
                              </span>
                            </div>
                            {tech.strengths.length > 0 && (
                              <p
                                style={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  fontSize: '12px',
                                  lineHeight: 1.5,
                                }}
                              >
                                ✓ {tech.strengths[0]}
                              </p>
                            )}
                            {tech.areas_for_improvement.length > 0 && (
                              <p style={{ color: '#fbbf24', fontSize: '12px', marginTop: '6px' }}>
                                💡 {tech.areas_for_improvement[0].fix}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {analysis.recommendations && analysis.recommendations.length > 0 && (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px',
                        }}
                      >
                        <CheckCircle2 size={16} style={{ color: 'var(--purple-600)' }} />
                        <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>
                          Coach Notes
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {analysis.recommendations.map((rec, i) => (
                          <div
                            key={i}
                            style={{
                              padding: '10px 12px',
                              background: 'rgba(var(--gold-rgb), 0.1)',
                              borderRadius: '8px',
                              borderLeft: '3px solid var(--purple-600)',
                            }}
                          >
                            <p
                              style={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '13px',
                                lineHeight: 1.5,
                              }}
                            >
                              {rec}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Highlights */}
                  {analysis.highlights && analysis.highlights.length > 0 && (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px',
                        }}
                      >
                        <Play size={16} style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>
                          Key Moments
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {analysis.highlights.map((highlight, i) => (
                          <div
                            key={i}
                            onClick={() => seekTo(highlight.timestamp_seconds)}
                            style={{
                              padding: '10px 12px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '10px',
                            }}
                          >
                            <span
                              style={{
                                padding: '2px 8px',
                                background: 'rgba(var(--purple-rgb), 0.2)',
                                borderRadius: '4px',
                                fontSize: '11px',
                                color: 'var(--purple-400)',
                                fontWeight: '600',
                                flexShrink: 0,
                              }}
                            >
                              {formatTime(highlight.timestamp_seconds)}
                            </span>
                            <p
                              style={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontSize: '12px',
                                lineHeight: 1.4,
                              }}
                            >
                              {highlight.reason}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              background: 'var(--gray-900)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              border: '1px solid var(--gray-700)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px' }}>
              Delete Video?
            </h3>
            <p
              style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '20px', fontSize: '14px' }}
            >
              This video will be moved to trash. You can restore it within 30 days.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button variant="secondary" fullWidth onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={handleDelete}
                loading={deleting}
                iconLeft={deleting ? undefined : <Trash2 size={16} />}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Paywall Modal */}
      {showAIPaywall && (
        <PaywallOverlay feature="ai_analysis" onClose={() => setShowAIPaywall(false)} />
      )}
    </div>
  );
}
